import {
  ItemIdentifiersFilterButton,
  MgSupplierItemSearchField,
} from '@retail/products/components';
import { SelectSupplier } from '@retail/suppliers/components';
import {
  DateFilter,
  GroupOptionToggle,
  ProductGroupMultiselectFilter,
  SideFilters,
  ToggleSwitch,
} from '@retail/components';
import { AssortmentTag, UserName } from '@retail/my-assortment/types';
import { AssortmentTagSelect } from '../AssortmentItems';
import { GroupOption, ProductGroupCount } from '@retail/products/types';
import { AddedByFilter } from './AddedByFilter';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { getISO_8601Date } from '@retail/utils';
import { SupplierBase } from '@retail/suppliers/types';
import { centralAssortmentsFilters, CentralAssortmentsFilters } from '@retail/my-assortment/utils';
import { useFilters } from '@retail/products/hooks';
import { useProductsTFunction } from '@retail/products/i18n';

interface Props {
  availableUserNames?: UserName[];
  showFilters: boolean;
  suppliers: SupplierBase[];
  selectedSubAssortments: AssortmentTag[];
  productGroupHierarchy: ProductGroupCount[];
  assortmentTags?: AssortmentTag[];
  hideIsTagged?: boolean;
  hideInAssortment?: boolean;
  showIncludeExpiredTags?: boolean;
  showFromMainSupplier?: boolean;
  onOpenItemIdentifiersFilter?: () => void;
}

export function CentralAssortmentFilters({
  availableUserNames,
  showFilters,
  suppliers,
  selectedSubAssortments,
  productGroupHierarchy,
  assortmentTags,
  hideIsTagged,
  hideInAssortment,
  showIncludeExpiredTags,
  showFromMainSupplier,
  onOpenItemIdentifiersFilter,
}: Props) {
  const t = useMyAssortmentTFunction();
  const tProducts = useProductsTFunction();
  const { filters, setFilters, updateFilter } =
    useFilters<CentralAssortmentsFilters>(centralAssortmentsFilters);

  const {
    inAssortment,
    isTagged,
    addedBy,
    validFrom,
    validTo,
    includeExpiredSubAssortments,
    fromMainSupplier,
    supplierNumber,
    searchQuery,
    productGroupNumbers,
  } = filters;

  return (
    <SideFilters showFilters={showFilters} sx={{ py: 1 }}>
      {inAssortment && !hideInAssortment && (
        <GroupOptionToggle
          label={t('myAssortment.status.inAssortment')}
          value={inAssortment}
          onChange={(option: GroupOption) =>
            setFilters({ inAssortment: option !== 'UNSPECIFIED' ? option : undefined })
          }
        />
      )}
      {isTagged && !hideIsTagged && (
        <GroupOptionToggle
          label={t('myAssortment.filter.subAssortment')}
          value={isTagged}
          justifyContent="space-between"
          allLabel={t('myAssortment.filter.all')}
          yesLabel={t('myAssortment.filter.with')}
          noLabel={t('myAssortment.filter.without')}
          onChange={(option: GroupOption) =>
            setFilters({ isTagged: option !== 'UNSPECIFIED' ? option : undefined })
          }
        />
      )}
      {showIncludeExpiredTags && (
        <ToggleSwitch
          justifyContent="space-between"
          toggle={!!includeExpiredSubAssortments}
          setToggle={() =>
            setFilters({ includeExpiredSubAssortments: !includeExpiredSubAssortments })
          }
          text={t('myAssortment.filter.includeExpiredTags')}
        />
      )}
      {showFromMainSupplier && (
        <ToggleSwitch
          justifyContent="space-between"
          toggle={fromMainSupplier}
          setToggle={() => setFilters({ fromMainSupplier: !fromMainSupplier })}
          text={tProducts('products.search.onlyProductOwners')}
        />
      )}

      {assortmentTags && (
        <AssortmentTagSelect
          includeUnselectedLevel={false}
          assortmentTags={assortmentTags}
          selectedTags={selectedSubAssortments}
          selectTagById={(tagId: number) => updateFilter('subAssortmentIds', tagId.toString())}
          resetTags={() => null}
        />
      )}
      {availableUserNames && (
        <AddedByFilter
          availableCreatedByUserNames={availableUserNames}
          createdByFilter={addedBy}
          setCreatedByFilter={(userId?: string) => setFilters({ addedBy: userId })}
        />
      )}

      <DateFilter
        label={t('myAssortment.filter.validFromAndIncluded')}
        value={validFrom ? new Date(validFrom) : null}
        onChange={(newDate) =>
          setFilters({ validFrom: newDate ? getISO_8601Date(newDate) : undefined })
        }
      />

      <DateFilter
        label={t('myAssortment.filter.validToAndIncluded')}
        value={validTo ? new Date(validTo) : null}
        onChange={(newDate) =>
          setFilters({ validTo: newDate ? getISO_8601Date(newDate) : undefined })
        }
      />

      <SelectSupplier
        suppliers={suppliers}
        selectedSupplier={supplierNumber ? Number(supplierNumber) : null}
        getSupplierId={(supplier: SupplierBase) => supplier.supplierNumber}
        getSupplierName={(supplier: SupplierBase) => supplier.name}
        selectSupplier={(supplierNumber) =>
          setFilters({ supplierNumber: supplierNumber ? `${supplierNumber}` : undefined })
        }
      />
      <MgSupplierItemSearchField
        size="small"
        width="100%"
        searchTerm={searchQuery ?? ''}
        onSubmitSearch={(query: string) => setFilters({ searchQuery: query })}
      />
      {onOpenItemIdentifiersFilter && (
        <ItemIdentifiersFilterButton onClick={onOpenItemIdentifiersFilter} />
      )}
      <ProductGroupMultiselectFilter
        productGroups={productGroupHierarchy}
        selectedProductGroups={productGroupNumbers}
        selectProductGroup={(groupNumber: string) =>
          updateFilter('productGroupNumbers', groupNumber)
        }
        getCount={(productGroup) => productGroup.count}
      />
    </SideFilters>
  );
}
